import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { Trophy, Megaphone, FileText, FileQuestion, Phone, GitBranch } from 'lucide-react';
import type { TopAccountItem } from '@respectly/shared-types';

interface TopAccountsTableProps {
  accounts: TopAccountItem[];
  isLoading?: boolean;
}

const COLUMNS = [
  { key: 'campaigns' as const, label: 'Campaigns', icon: Megaphone, color: '#3b82f6' },
  { key: 'documents' as const, label: 'Docs', icon: FileText, color: '#8b5cf6' },
  { key: 'forms' as const, label: 'Forms', icon: FileQuestion, color: '#10b981' },
  { key: 'voice' as const, label: 'Voice', icon: Phone, color: '#f59e0b' },
  { key: 'workflows' as const, label: 'Flows', icon: GitBranch, color: '#a855f7' },
];

function MiniBar({ value, max, color }: { value: number; max: number; color: string }) {
  const width = max > 0 ? Math.max((value / max) * 100, value > 0 ? 4 : 0) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] tabular-nums font-semibold text-slate-700 dark:text-slate-300 w-8 text-right shrink-0">
        {value > 0 ? value.toLocaleString() : '—'}
      </span>
      <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${width}%`, backgroundColor: color, opacity: value > 0 ? 0.7 : 0 }}
        />
      </div>
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="text-base">🥇</span>;
  if (rank === 2) return <span className="text-base">🥈</span>;
  if (rank === 3) return <span className="text-base">🥉</span>;
  return (
    <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 w-5 text-center block">
      {rank}
    </span>
  );
}

export function TopAccountsTable({ accounts, isLoading }: TopAccountsTableProps) {
  if (isLoading) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60 h-full">
        <CardHeader className="pb-3">
          <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const maxTotal = Math.max(...accounts.map((a) => a.total), 1);
  const maxPerColumn = new Map<string, number>(
    COLUMNS.map((c) => [c.key, Math.max(...accounts.map((a) => a[c.key]), 1)])
  );

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-yellow-500/10 dark:bg-yellow-500/20 flex items-center justify-center">
            <Trophy className="h-4 w-4 text-yellow-500" />
          </div>
          <div>
            <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Most Active Accounts
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">Ranked by total activity this period</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {accounts.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-slate-400 dark:text-slate-500">No account activity yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/40">
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px] w-8">#</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Account</th>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10px] min-w-[100px]">
                      <div className="flex items-center gap-1" style={{ color: c.color }}>
                        <c.icon className="h-2.5 w-2.5" />
                        {c.label}
                      </div>
                    </th>
                  ))}
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px] min-w-[80px]">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {accounts.map((account, index) => (
                  <tr
                    key={account.tenantId}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <RankBadge rank={index + 1} />
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-semibold text-slate-800 dark:text-slate-200 max-w-[140px] truncate block">
                        {account.tenantName}
                      </span>
                    </td>
                    {COLUMNS.map((c) => (
                      <td key={c.key} className="px-3 py-2.5">
                        <MiniBar
                          value={account[c.key]}
                          max={maxPerColumn.get(c.key) ?? 1}
                          color={c.color}
                        />
                      </td>
                    ))}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-bold tabular-nums text-slate-900 dark:text-slate-100">
                          {account.total.toLocaleString()}
                        </span>
                        <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full min-w-[40px]">
                          <div
                            className="h-full rounded-full bg-slate-400 dark:bg-slate-500"
                            style={{ width: `${Math.max((account.total / maxTotal) * 100, 4)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
